// Карточка объекта — выезжающая справа панель (DESIGN §4: один паттерн для лида, объявления, креатива).
// Закрывается крестиком, Esc и кликом по затемнению; фокус уходит в панель и возвращается на место.
import { IconButton } from '@autoaff/ui';
import { ArrowLeft, X } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { type ReactNode, useEffect, useRef } from 'react';

export function SidePanel({
  open,
  label,
  onClose,
  head,
  footer,
  children,
}: {
  open: boolean;
  /** Название для экранного диктора: «Карточка лида». */
  label: string;
  onClose: () => void;
  head: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const reduce = useReducedMotion();
  const panel = useRef<HTMLElement>(null);
  const back = useRef<Element | null>(null);

  useEffect(() => {
    if (!open) return;
    back.current = document.activeElement;
    panel.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      // Палитра команд и чат поверх карточки закрываются первыми
      if (e.key === 'Escape' && !document.querySelector('.pal, .chat-panel')) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (back.current instanceof HTMLElement) back.current.focus({ preventScroll: true });
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="scrim"
            className="scrim hide-m"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.18 }}
            onClick={onClose}
          />
          <motion.section
            key="panel"
            ref={panel}
            tabIndex={-1}
            className="side-panel"
            role="dialog"
            aria-modal="true"
            aria-label={label}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 40 }}
          >
            <header className="chat-head">
              <IconButton label="Закрыть" className="only-m" onClick={onClose}>
                <ArrowLeft />
              </IconButton>
              <div className="sp-title">{head}</div>
              <IconButton label="Закрыть" className="hide-m" onClick={onClose}>
                <X />
              </IconButton>
            </header>
            <div className="sp-body">{children}</div>
            {footer && <footer className="sp-foot">{footer}</footer>}
          </motion.section>
        </>
      )}
    </AnimatePresence>
  );
}
